import classNames from 'classnames'
import React from 'react'
import { Icon, Spinner } from '@blueprintjs/core'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  IUI2FormSectionErrorContext,
  UI2FormSectionErrorContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-error-context'
import {
  IUI2FormSectionFillContext,
  UI2FormSectionFillContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-fill-context'
import { UI2FormSectionIdContext } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-id-context'
import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'
import { UI2FormSectionSubmitContext } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-submit-context'
import { UI2FormSectionProps } from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-props'
import {
  IUI2FormContext,
  UI2FormContext,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-form-context'
import { FormDensity } from 'shared-libs/components/ui2/forms/ui2-form/ui2-form-props'
import { UI2WizardContext } from 'shared-libs/components/ui2/forms/ui2-form/ui2-wizard-context'
import { UI2FormSectionStatus } from 'shared-libs/components/ui2/forms/ui2-form/wizard-controller'

import { REVEAL_EVENT } from 'browser/components/atomic-elements/atoms/form-field/form-field-shell/reveal-form-field'

import { UI2Label } from '../../fields/field-ui/label/label'

import './_ui2-form-section.scss'

/**
 * @uiComponent
 */
interface IUI2FormSectionProps extends UI2FormSectionProps {
  formDensity?: FormDensity
  frames?: FramesManager
  translationTable?: any
  children?: React.ReactNode
}

interface IUI2FormSectionState {
  isCollapsed: boolean
  hasReceivedError: boolean
  errorCount: number
  /** Once true, descendant fields paint their `is required` errors immediately. */
  showRequiredErrors: boolean
}

export class UI2FormSection extends React.Component<IUI2FormSectionProps, IUI2FormSectionState> {
  public static contextType = UI2WizardContext
  public declare context: React.ContextType<typeof UI2WizardContext>

  // The section's `value` binds its own status field, not a container path for
  // descendant fields. See EntityRenderer.createChildFrame — descendants'
  // `value` bindings resolve from the entity root when this is set.
  public static isolatesChildValueScope = true

  private bodyRef = React.createRef<HTMLDivElement>()
  private erroredFieldIds = new Set<string>()
  private unfilledFieldIds = new Set<string>()
  private sectionId = `ui2FormSection:${Math.random().toString(36).slice(2)}`
  private lastEmittedStatus: UI2FormSectionStatus | undefined
  private parentErrorContext: IUI2FormSectionErrorContext | null = null
  private parentFillContext: IUI2FormSectionFillContext | null = null
  private focusTargetContext: IFormFocusTargetContext | null = null

  // Every field the entity renderer wraps with UI2FormFieldErrorReporter
  // reports through this context — feeds into wizard/section aggregation.
  private childContext: IUI2FormSectionErrorContext = {
    reportError: (id, hasError) => {
      if (id === this.sectionId) return
      const wasEmpty = this.erroredFieldIds.size === 0
      if (hasError) this.erroredFieldIds.add(id)
      else this.erroredFieldIds.delete(id)
      const isEmpty = this.erroredFieldIds.size === 0
      if (!isEmpty && this.state.isCollapsed) {
        this.setState({ isCollapsed: false })
      }
      const nextHasReceived = this.state.hasReceivedError || !isEmpty
      if (
        this.state.errorCount !== this.erroredFieldIds.size ||
        nextHasReceived !== this.state.hasReceivedError
      ) {
        this.setState({ errorCount: this.erroredFieldIds.size, hasReceivedError: nextHasReceived })
      }
      if (wasEmpty !== isEmpty) {
        this.parentErrorContext?.reportError(this.sectionId, !isEmpty)
      }
      if (
        this.context?.isWizard &&
        this.context.activeSectionId === this.sectionId &&
        this.getEffectiveNextMode() === 'automatic' &&
        isEmpty &&
        nextHasReceived
      ) {
        // Automatic mode is for extremely simple sections — never surface
        // wizard-driven error highlighting. Just auto-advance when clean.
        this.context.advance(this.sectionId)
      }
    },
  }

  private childFillContext: IUI2FormSectionFillContext = {
    reportUnfilled: (id, isUnfilled) => {
      if (id === this.sectionId) return
      const wasEmpty = this.unfilledFieldIds.size === 0
      if (isUnfilled) this.unfilledFieldIds.add(id)
      else this.unfilledFieldIds.delete(id)
      const isEmpty = this.unfilledFieldIds.size === 0
      if (wasEmpty !== isEmpty) {
        this.parentFillContext?.reportUnfilled(this.sectionId, !isEmpty)
      }
    },
  }

  constructor(props) {
    super(props)
    this.state = {
      isCollapsed: props.isCollapsed ?? false,
      hasReceivedError: false,
      errorCount: 0,
      showRequiredErrors: false,
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isCollapsed !== this.props.isCollapsed) {
      this.setState({ isCollapsed: nextProps.isCollapsed ?? false })
    }
  }

  private wasActiveLastRender = false

  public componentDidUpdate() {
    this.emitStatus()
    const wizardCtx = this.context
    if (!wizardCtx?.isWizard) {
      this.wasActiveLastRender = false
      return
    }
    const isNowActive = wizardCtx.activeSectionId === this.sectionId
    const justActivated = isNowActive && !this.wasActiveLastRender
    this.wasActiveLastRender = isNowActive
    // Automatic sections normally auto-advance when their childContext sees a
    // reportError transition. If the user arrives at a clean automatic section
    // (all fields already valid — e.g. after wizard replay lands them on the
    // previous step and they skip forward, or any manual path that reaches a
    // pre-satisfied automatic step), no transition ever fires and the section
    // hangs with no Next button. Fire the same advance path on activation to
    // cover that case. Replay drives its own advancement — don't race with it.
    if (
      justActivated &&
      !wizardCtx.isReplaying &&
      this.getEffectiveNextMode() === 'automatic' &&
      this.erroredFieldIds.size === 0
    ) {
      wizardCtx.advance(this.sectionId)
    }
  }

  public componentDidMount() {
    this.bodyRef.current?.addEventListener(REVEAL_EVENT, this.handleReveal)
    if (this.context?.isWizard) {
      this.context.register({
        id: this.sectionId,
        title: this.props.title,
        entryPredicate: this.props.entryPredicate,
        nextMode: this.props.nextMode ?? 'submit',
        allowSkip: !!this.props.allowSkip,
      })
    }
    this.emitStatus()
  }

  /**
   * Push the section's derived status out through the bound `onChange`. In
   * wizard mode the status comes from the form's projection of the wizard
   * queue and is unavailable until the section has registered — nothing is
   * emitted on that first render. In default mode there is no queue, so the
   * section mirrors how `ui2:form` derives its own default-mode status: clean
   * is 'complete', anything else is 'upcoming'.
   */
  private emitStatus() {
    const { onChange, value } = this.props
    if (!onChange) return
    const wizardCtx = this.context
    const status: UI2FormSectionStatus | undefined = wizardCtx?.isWizard
      ? wizardCtx.sectionStatuses[this.sectionId]
      : this.state.errorCount === 0
        ? 'complete'
        : 'upcoming'
    if (status == null) return
    if (this.lastEmittedStatus === status) return
    if (value === status) {
      this.lastEmittedStatus = status
      return
    }
    this.lastEmittedStatus = status
    onChange(status)
  }

  public componentWillUnmount() {
    this.bodyRef.current?.removeEventListener(REVEAL_EVENT, this.handleReveal)
    if (this.erroredFieldIds.size > 0) {
      this.parentErrorContext?.reportError(this.sectionId, false)
    }
    if (this.unfilledFieldIds.size > 0) {
      this.parentFillContext?.reportUnfilled(this.sectionId, false)
    }
    if (this.context?.isWizard) {
      this.context.deregister(this.sectionId)
    }
  }

  public render() {
    return (
      <FormFocusTargetContext.Consumer>
        {(focusCtx) => {
          this.focusTargetContext = focusCtx
          return (
            <UI2FormSectionErrorContext.Consumer>
              {(parentErrorCtx) => {
                this.parentErrorContext = parentErrorCtx
                return (
                  <UI2FormSectionFillContext.Consumer>
                    {(parentFillCtx) => {
                      this.parentFillContext = parentFillCtx
                      return this.renderInner()
                    }}
                  </UI2FormSectionFillContext.Consumer>
                )
              }}
            </UI2FormSectionErrorContext.Consumer>
          )
        }}
      </FormFocusTargetContext.Consumer>
    )
  }

  private renderInner() {
    const {
      formDensity = 'default',
      isReadOnly,
      isDisabled,
      isRequired,
      isOptional,
      enablePrefilledMode,
      isHorizontalLayout,
      title,
      children,
    } = this.props
    const { isCollapsed } = this.state
    const wizardCtx = this.context
    const isWizard = !!wizardCtx?.isWizard
    // For regular wizard mode, only the active section is visible. For stacked mode, the active section is interactive, completed sections are read-only, and skipped and incomplete sections are hidden.
    const isHiddenByWizard =
      isWizard && wizardCtx.mode === 'wizard'
        ? wizardCtx.activeSectionId !== this.sectionId
        : wizardCtx?.mode === 'stacked' &&
          !this.getIsWizardStepComplete() &&
          wizardCtx.activeSectionId !== this.sectionId
    const isActiveByWizard = isWizard && wizardCtx.activeSectionId === this.sectionId
    const renderIsCompleteByWizard =
      isWizard && wizardCtx.mode === 'stacked' && this.getIsWizardStepComplete()
    const isReplaying = isWizard && !!wizardCtx?.isReplaying
    const childFormContext: IUI2FormContext = {
      formDensity: renderIsCompleteByWizard ? 'compact' : formDensity,
      isReadOnly: isReadOnly || renderIsCompleteByWizard,
      isDisabled,
      isRequired,
      isOptional,
      enablePrefilledMode,
      isHorizontalLayout,
      ...(renderIsCompleteByWizard ? { inlineHelpText: null } : undefined),
    }
    return (
      <UI2FormContext.Provider value={childFormContext}>
        <UI2FormSectionIdContext.Provider value={this.sectionId}>
          <UI2FormSectionErrorContext.Provider value={this.childContext}>
            <UI2FormSectionFillContext.Provider value={this.childFillContext}>
              <UI2FormSectionSubmitContext.Provider value={this.state.showRequiredErrors}>
                <div
                  className={classNames('ui2-formSection', `ui2-formSection--${formDensity}`, {
                    'ui2-formSection--complete': renderIsCompleteByWizard,
                    'is-active--wizard': isActiveByWizard && wizardCtx?.mode === 'wizard',
                    'is-active--stacked': isActiveByWizard && wizardCtx?.mode === 'stacked',
                  })}
                  style={isHiddenByWizard ? { display: 'none' } : undefined}
                >
                  {title || !isWizard ? ( // since wizard mode doesn't have collapsing or need additional section separation, header is not needed when there is no title 
                    <div className={classNames('ui2-formSectionHeader')}>
                      {this.renderHeaderContent()}
                    </div>
                  ) : null}
                  <div
                    ref={this.bodyRef}
                    className={classNames(
                      'ui2-formSectionBody',
                      `ui2-formSectionBody--${formDensity}`,
                      {
                        'is-collapsed': isCollapsed,
                        'ui2-form-sectionBody--replaying': isReplaying && !isHiddenByWizard,
                      }
                    )}
                  >
                    {children}
                  </div>
                  {isReplaying && !isHiddenByWizard && (
                    <div className="ui2-form-sectionReplay">
                      <Spinner size={20} />
                    </div>
                  )}
                  {isWizard && isActiveByWizard && !isReplaying && this.renderWizardFooter()}
                </div>
              </UI2FormSectionSubmitContext.Provider>
            </UI2FormSectionFillContext.Provider>
          </UI2FormSectionErrorContext.Provider>
        </UI2FormSectionIdContext.Provider>
      </UI2FormContext.Provider>
    )
  }

  /**
   * A previously-completed automatic-mode section is treated as submit-mode on
   * revisit — the user must manually press Next to reconfirm any edits, and no
   * auto-advance fires.
   */
  private getEffectiveNextMode(): 'submit' | 'automatic' {
    const declared = this.props.nextMode ?? 'submit'
    if (declared !== 'automatic') return 'submit'
    const wasEverCompleted = !!this.context?.progressSteps.find((s) => s.id === this.sectionId)
      ?.wasEverCompleted
    return wasEverCompleted ? 'submit' : 'automatic'
  }

  private getIsWizardStepComplete(): boolean {
    const wizardCtx = this.context
    if (!wizardCtx?.isWizard) return false
    const step = wizardCtx.progressSteps.find((s) => s.id === this.sectionId)
    return !!step?.isCompleted
  }

  private renderWizardFooter() {
    const { allowSkip } = this.props
    const nextMode = this.getEffectiveNextMode()
    const wizardCtx = this.context!
    const hasErrors = this.state.errorCount > 0
    const isActiveFinalStep = wizardCtx.isFinalStep && wizardCtx.activeSectionId === this.sectionId
    // Skip doesn't make sense on the final step — nothing else to route to.
    const canSkip = allowSkip && !isActiveFinalStep && wizardCtx.mode === 'wizard' // don't allow skipping in stacked mode since the ux is kind of odd
    return (
      <div className="ui2-form-sectionFooter">
        {wizardCtx.canGoBack && (
          <button type="button" className="ui2-form-sectionFooter__back" onClick={wizardCtx.back}>
            Back
          </button>
        )}
        {canSkip && (
          <button
            type="button"
            className="ui2-form-sectionFooter__skip"
            onClick={() => wizardCtx.skip(this.sectionId)}
          >
            Skip
          </button>
        )}
        {nextMode === 'submit' && (
          <button
            type="button"
            className={classNames('ui2-form-sectionFooter__next', {
              'ui2-form-sectionFooter__next--muted': hasErrors,
            })}
            onClick={this.handleNext}
          >
            {wizardCtx.isFinalStep && wizardCtx.activeSectionId === this.sectionId
              ? 'Complete'
              : 'Next'}
          </button>
        )}
      </div>
    )
  }

  private handleNext = () => {
    if (this.erroredFieldIds.size > 0) {
      // Surface field-level red glow on the first blocked Next click. Every
      // subsequent click keeps the flag on so newly-emptied fields also glow.
      this.setState({ hasReceivedError: true, showRequiredErrors: true }, () => {
        // Wait for the shell's showRequiredErrors flip so a fresh
        // hasError() read returns true for empty-required fields.
        this.focusTargetContext?.focusFirstError({ sectionId: this.sectionId })
      })
      return
    }
    this.context?.advance(this.sectionId)
  }

  private renderHeaderContent() {
    const { title, tooltip, inlineHelpText, isCollapsable, translationTable, frames } = this.props
    const { isCollapsed } = this.state
    const resolvedTranslationTable = Object.assign(
      {},
      translationTable,
      frames?.getContext('translationTable')
    )
    const hasTitle = !!title
    if (!hasTitle && !isCollapsable) return null
    return (
      <>
        {hasTitle && (
          <UI2Label
            className="ui2-formSectionHeader-label"
            labelText={title}
            tooltipText={typeof tooltip === 'string' ? tooltip : tooltip?.join('\n')}
            inlineHelpText={
              typeof inlineHelpText === 'string' ? inlineHelpText : inlineHelpText?.join('\n')
            }
            translationTable={resolvedTranslationTable}
          />
        )}
        {isCollapsable && (
          <button
            className="ui2-formSectionHeader-collapseButton"
            onClick={this.toggleCollapsed}
            type="button"
            aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
          >
            <Icon icon={isCollapsed ? 'chevron-down' : 'chevron-up'} size={14} />
          </button>
        )}
      </>
    )
  }

  private toggleCollapsed = () => {
    this.setState((prev) => ({ isCollapsed: !prev.isCollapsed }))
  }

  private handleReveal = () => {
    if (this.state.isCollapsed) {
      this.setState({ isCollapsed: false })
    }
  }
}
